import React from 'react';
import {
  Settings,
  Github,
  Key,
  Copy,
  Plus,
  Trash2,
  CheckCircle2,
  ShieldAlert,
  TerminalSquare,
  ExternalLink,
} from 'lucide-react';

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Code,
  Container,
  CopyButton,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import useUser from "./userProvider.jsx";
import useConfig from "./configProvider";

import RepositoryTable from './RepositoryTable.jsx';

export default function ConfigurationPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState('shadow');

  const {userInfo} = useUser();
  const config = useConfig();

  const baseUrl = config?.data?.serverBaseUrl || 'https://timestampgit.dev';

  const codeSnippet = `- name: Secure Prior Art Timestamp
  run: |
    curl -X POST ${baseUrl}/api/timestamp \\
      -H "Authorization: Bearer \${{ secrets.TIMESTAMP_API_KEY }}" \\
      -H "Content-Type: application/json" \\
      -d '{
        "repo": "\${{ github.repository }}",
        "commit": "\${{ github.sha }}"
      }'`;

  const repoRows = [
    {
      name: 'acme-corp/frontend-react',
      status: 'active',
      lastAnchored: t('Today, 00:05 UTC'),
      proofs: '1,245',
    },
    {
      name: 'acme-corp/payment-service-go',
      status: 'active',
      lastAnchored: t('Today, 00:05 UTC'),
      proofs: '892',
    },
    {
      name: 'acme-corp/legacy-api',
      status: 'paused',
      lastAnchored: t('Oct 12, 2025'),
      proofs: '412',
    },
  ];

  const apiRows = [
    {
      keyName: 'Production Core Action',
      prefix: 'ts_live_8f92...',
      created: t('Jan 15, 2026'),
      lastUsed: t('2 hours ago'),
    },
  ];

  return (
    <Box mih="100vh" py={40}>
      <Container size="md">
        <Stack gap="lg">
          <Paper withBorder radius="md" p="md">
            <Group gap="sm" align="flex-start" wrap="nowrap">
              <ThemeIcon size={42} radius="md" variant="light" color="teal">
                <Settings size={22} />
              </ThemeIcon>
              <Stack gap={2}>
                <Title order={1} fz="2.25rem" fw={800}>
                  {t('Workspace Configuration')}
                </Title>
                <Text c="dimmed" size="lg">
                  {t('Manage your shadow repositories, tracked projects, and API keys.')}
                </Text>
              </Stack>
            </Group>
          </Paper>

          <Tabs
            value={activeTab}
            onChange={(value) => setActiveTab(value || 'shadow')}
            variant="pills"
            radius="md"
            color="teal"
            styles={{
              tab: {
                fontWeight: 700,
              },
            }}
          >
            <Paper withBorder radius="md" p={4}>
              <Tabs.List grow>
                <Tabs.Tab value="shadow" leftSection={<Settings size={18} />} fw={700}>
                {t('Shadow Repo')}
                </Tabs.Tab>
                <Tabs.Tab value="repos" leftSection={<Github size={18} />} fw={700}>
                {t('Monitored Repos')}
                </Tabs.Tab>
                <Tabs.Tab value="api" leftSection={<Key size={18} />} fw={700}>
                {t('Zero-Knowledge API Keys')}
                </Tabs.Tab>
              </Tabs.List>
            </Paper>

            <Tabs.Panel value="shadow" pt="md">
              <Paper radius="md" p="lg" withBorder>
                <Stack gap="lg">
                  <Group gap="sm">
                    <ThemeIcon variant="light" color="teal" radius="xl">
                      <ShieldAlert size={16} />
                    </ThemeIcon>
                    <Title order={3}>{t('Destination Repository')}</Title>
                  </Group>

                  <Text c="dimmed" size="sm">
                    {t('This is the ')}
                    <Text component="span" fw={700}>
                      {t('isolated, private GitHub repository')}
                    </Text>
                    {t(' where we push your cryptographic ')}
                    <Code>.ots</Code>
                    {t(' proofs and daily audit ledgers. We require ')}
                    <Text component="span" fw={700}>
                      {t('Write Access')}
                    </Text>
                    {t(' to this specific repository only.')}
                  </Text>

                  <TextInput
                    label={t('Connected Shadow Repository')}
                    value="acme-corp/audit-timestamps-2026"
                    disabled
                  />

                  <Group>
                    <Button variant="default" leftSection={<ExternalLink size={16} />}>
                      {t('View in GitHub')}
                    </Button>
                  </Group>

                  <Select
                    label={t('Directory Structure')}
                    description={t('Choose how you want proofs organized inside your Shadow Repo.')}
                    defaultValue="date"
                    data={[
                      { value: 'date', label: t('By Date (e.g., /2026/03/18/proofs)') },
                      { value: 'repo', label: t('By Repository (e.g., /frontend-app/2026/proofs)') },
                      { value: 'flat', label: t('Flat List (Not recommended for large workspaces)') },
                    ]}
                  />

                  <Divider />

                  <Group>
                    <Button variant="light" color="red">
                      {t('Disconnect Shadow Repo')}
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="repos" pt="md">
              <Paper radius="md" p="lg" withBorder>
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                      <Title order={3}>{t('Standard Mode Repositories')}</Title>
                      <Text c="dimmed" size="sm">
                        {t('Repositories currently being tracked automatically via the GitHub App.')}
                      </Text>
                    </Stack>
                    <Button variant="filled" color="teal" leftSection={<Plus size={16} />}>
                      {t('Add Repository')}
                    </Button>
                  </Group>

                  <RepositoryTable user={userInfo?.sub} />

                  <ScrollArea>
                    <Table striped highlightOnHover withTableBorder withColumnBorders>
                      <thead>
                        <tr>
                          <th>{t('Repository')}</th>
                          <th>{t('Status')}</th>
                          <th>{t('Last Anchored')}</th>
                          <th>{t('Total Proofs')}</th>
                          <th>{t('Actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repoRows.map((row) => (
                          <tr key={row.name}>
                            <td>
                              <Box px="sm" py={4}>
                                <Text fw={600}>{row.name}</Text>
                              </Box>
                            </td>
                            <td>
                              {row.status === 'active' ? (
                                <Badge variant="light" color="teal" leftSection={<CheckCircle2 size={10} />}>
                                  {t('Active')}
                                </Badge>
                              ) : (
                                <Badge variant="light" color="blue">
                                  {t('Paused')}
                                </Badge>
                              )}
                            </td>
                            <td>{row.lastAnchored}</td>
                            <td>{row.proofs}</td>
                            <td>
                              {row.status === 'active' ? (
                                <Button variant="subtle" color="red" leftSection={<Trash2 size={14} />}>
                                  {t('Stop Tracking')}
                                </Button>
                              ) : (
                                <Button variant="default">{t('Resume')}</Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ScrollArea>
                </Stack>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="api" pt="md">
              <Paper radius="md" p="lg" withBorder>
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                      <Group gap="sm">
                        <ThemeIcon variant="light" color="blue" radius="xl">
                          <TerminalSquare size={16} />
                        </ThemeIcon>
                        <Title order={3}>{t('Enterprise ZK Integration')}</Title>
                      </Group>
                      <Text c="dimmed" size="sm" maw={600}>
                        {t('Use these API keys in your CI/CD pipelines (GitHub Actions, GitLab CI, Jenkins) to push commit hashes securely.')}
                      </Text>
                    </Stack>
                    <Button variant="filled" color="teal" leftSection={<Plus size={16} />}>
                      {t('Generate New Key')}
                    </Button>
                  </Group>

                  <ScrollArea>
                    <Table
                      striped
                      highlightOnHover
                      withTableBorder
                      withColumnBorders
                      horizontalSpacing="md"
                      verticalSpacing="sm"
                      styles={{
                        th: { padding: '12px 14px' },
                        td: { padding: '12px 14px' },
                      }}
                    >
                      <thead>
                        <tr>
                          <th>{t('Key Name')}</th>
                          <th>{t('Prefix')}</th>
                          <th>{t('Created')}</th>
                          <th>{t('Last Used')}</th>
                          <th>{t('Actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiRows.map((row) => (
                          <tr key={row.keyName}>
                            <td>
                              <Box px="sm" py={4}>
                                <Text fw={600}>{row.keyName}</Text>
                              </Box>
                            </td>
                            <td>
                              <Code>{row.prefix}</Code>
                            </td>
                            <td>{row.created}</td>
                            <td>{row.lastUsed}</td>
                            <td>
                              <Button variant="subtle" color="red" leftSection={<Trash2 size={14} />}>
                                {t('Revoke')}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ScrollArea>

                  <Stack gap="xs">
                    <Title order={4}>{t('GitHub Action Implementation')}</Title>
                    <Text c="dimmed" size="sm">
                      {t('Add your API key as a repository secret named ')}
                      <Code>TIMESTAMP_API_KEY</Code>
                      {t(' and add this step to your deployment workflow.')}
                    </Text>
                  </Stack>

                  <Paper withBorder p="md" radius="md">
                    <Group justify="space-between" align="center" mb="sm">
                      <Text fw={600} size="sm">
                        {t('Copy to clipboard')}
                      </Text>
                      <CopyButton value={codeSnippet} timeout={2000}>
                        {({ copied, copy }) => (
                          <ActionIcon
                            variant={copied ? 'light' : 'default'}
                            color={copied ? 'teal' : 'gray'}
                            onClick={copy}
                            title={t('Copy to clipboard')}
                          >
                            {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                          </ActionIcon>
                        )}
                      </CopyButton>
                    </Group>
                    <Code block>{codeSnippet}</Code>
                  </Paper>
                </Stack>
              </Paper>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </Box>
  );
}
